import {
    GalleryImage,
    Image,
    ResponsiveImgList
} from 'shared/components/Gallery/Gallery.types';
import { SubImage } from '../ListingCard.types';
import placeHolderImage from 'public/assets/place-holder-image.jpg';

type ResponsiveImage = {
    width: number;
    src: string | null | undefined;
};

type ListingImage =
    | {
          src: string | null | undefined;
          caption: string | null;
          responsiveImgList?: ResponsiveImage[];
      }
    | undefined;

const convertSubImageResponseToGalleryImage = (
    image: SubImage
): GalleryImage => {
    return {
        src: image.large,
        alt: image.caption,
        responsiveImgList: {
            original: [
                {
                    width: 480,
                    src: image.small
                },
                {
                    width: 960,
                    src: image.large
                }
            ]
        }
    };
};

const filterEmptyResponsiveImages = (
    responsiveImages: ResponsiveImage[] | undefined
): ResponsiveImgList | undefined => {
    if (!responsiveImages || responsiveImages.length < 2) {
        return undefined;
    }

    const [first, second] = responsiveImages;

    const firstToImage: Image = {
        src: first.src ?? placeHolderImage,
        width: first.width
    };
    const secondToImage: Image = {
        src: second.src ?? placeHolderImage,
        width: second.width
    };

    return { original: [firstToImage, secondToImage] };
};

const convertImagePropsToGalleryImage = (image: ListingImage): GalleryImage => {
    if (image) {
        const responsiveImgList = filterEmptyResponsiveImages(
            image.responsiveImgList
        );

        if (image.src && responsiveImgList)
            return {
                src: image.src,
                alt: image.caption || undefined,
                responsiveImgList: responsiveImgList
            };
    }

    return {
        src: placeHolderImage,
        alt: undefined,
        responsiveImgList: {
            original: [
                { src: placeHolderImage, width: 480 },
                { src: placeHolderImage, width: 960 }
            ]
        }
    };
};

const convertImagePropsResponseToSubImage = (image: ListingImage) => {
    if (!image)
        return {
            small: placeHolderImage,
            large: placeHolderImage,
            caption: ''
        };

    const smallImage = image?.responsiveImgList?.find(
        (responsiveImage) => responsiveImage.width < 960 && responsiveImage.src
    );

    const definedImageOrPlaceholder = smallImage
        ? smallImage.src
        : placeHolderImage;

    return {
        small: definedImageOrPlaceholder,
        large: definedImageOrPlaceholder,
        caption: image.caption ?? ''
    };
};

const mapSubImageResponseToGalleryImages = (
    otherPropertyImages: SubImage[]
) => {
    if (!otherPropertyImages) return [];

    return otherPropertyImages.map((image) =>
        convertSubImageResponseToGalleryImage(image)
    );
};

export const mapPropertyImageResponseToGalleryImages = (
    image: ListingImage,
    otherPropertyImages: SubImage[]
): GalleryImage[] => {
    const firstImage = convertImagePropsToGalleryImage(image);

    const otherImages: GalleryImage[] = mapSubImageResponseToGalleryImages(
        otherPropertyImages
    );

    return [firstImage, ...otherImages];
};

export const mapPropertyImageResponseToSrc = (
    image: ListingImage,
    otherPropertyImages: SubImage[]
): SubImage[] => {
    const firstImage: SubImage = convertImagePropsResponseToSubImage(image);

    return [firstImage, ...otherPropertyImages];
};
